import { z } from "zod";
import { passportStableIdSchema } from "./passport-catalogue.js";

export const observationStepModeSchema = z.enum(["critical", "protocol", "deviation_only"]);
export const observationSectionModeSchema = z.enum(["critical_count", "deviation_only"]);

export const observationStepOutcomeSchema = z.enum(["seen_ok", "not_ok", "deviation", "not_applicable"]);
export const observationOralOutcomeSchema = z.enum(["sufficient", "insufficient"]);
export const observationAttitudeOutcomeSchema = z.enum(["sufficient", "attention", "insufficient"]);

export const observationProtocolStepSchema = z
  .object({
    id: passportStableIdSchema,
    number: z.number().int().positive(),
    title: z.string().min(1),
    skillCode: z.string().min(1),
    mode: observationStepModeSchema,
  })
  .strict();

export const observationProtocolSectionSchema = z
  .object({
    id: passportStableIdSchema,
    title: z.string().min(1),
    mode: observationSectionModeSchema,
    stepIds: z.array(passportStableIdSchema).min(1),
  })
  .strict();

export const observationProtocolOralQuestionSchema = z
  .object({
    id: passportStableIdSchema,
    prompt: z.string().min(1),
    skillCode: z.string().min(1),
    keyPoints: z.string().min(1),
  })
  .strict();

export const observationProtocolAttitudeSchema = z
  .object({
    criterionId: passportStableIdSchema,
    code: z.string().min(1),
    title: z.string().min(1),
    indicators: z.string().min(1),
  })
  .strict();

export const observationProtocolSchema = z
  .object({
    stampId: passportStableIdSchema,
    title: z.string().min(1),
    sections: z.array(observationProtocolSectionSchema).min(1),
    steps: z.array(observationProtocolStepSchema).min(1),
    oralQuestions: z.array(observationProtocolOralQuestionSchema).min(1),
    attitudes: z.array(observationProtocolAttitudeSchema).min(1),
  })
  .strict();

export const observationStepAnswerSchema = z
  .object({
    stepId: passportStableIdSchema,
    outcome: observationStepOutcomeSchema,
    note: z.string().trim().max(1_000).nullable(),
  })
  .strict()
  .superRefine((value, context) => {
    if ((value.outcome === "deviation" || value.outcome === "not_ok") && !value.note) {
      context.addIssue({
        code: "custom",
        path: ["note"],
        message: "Een toelichting is verplicht bij een afwijking of 'niet goed'.",
      });
    }
  });

export const observationOralAnswerSchema = z
  .object({
    questionId: passportStableIdSchema,
    outcome: observationOralOutcomeSchema,
  })
  .strict();

export const observationAttitudeAnswerSchema = z
  .object({
    criterionId: passportStableIdSchema,
    outcome: observationAttitudeOutcomeSchema,
    note: z.string().trim().max(1_000).nullable(),
  })
  .strict()
  .superRefine((value, context) => {
    if (value.outcome !== "sufficient" && !value.note) {
      context.addIssue({
        code: "custom",
        path: ["note"],
        message: "Een toelichting is verplicht bij een aandachtspunt of onvoldoende.",
      });
    }
  });

export const observationProtocolResultSchema = z
  .object({
    steps: z.array(observationStepAnswerSchema),
    oral: observationOralAnswerSchema,
    attitudes: z.array(observationAttitudeAnswerSchema).min(1),
  })
  .strict();

export type ObservationProtocol = z.infer<typeof observationProtocolSchema>;
export type ObservationProtocolResult = z.infer<typeof observationProtocolResultSchema>;
export type ObservationStepOutcome = z.infer<typeof observationStepOutcomeSchema>;
export type ObservationAttitudeOutcome = z.infer<typeof observationAttitudeOutcomeSchema>;
export type ObservationOralOutcome = z.infer<typeof observationOralOutcomeSchema>;
